import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Logo from "../../res/logo.png";
import { getVisibleNavigations } from "../../util/General";

// Theme constants
const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 60;
const LOGO_SIZE = 150;

const Sidebar = ({
  collapsible = true,
  modal = false,
  onDismissRequest,
  screenStack,
  setScreenStack,
  onOpenSidebar,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleNavigations = getVisibleNavigations();
  const [collapsed, setCollapsed] = useState(modal); // Collapsed on mobile
  const [menuCollapseState, setMenuCollapseState] = useState({
    [visibleNavigations.findIndex((nav) => nav.text === "Services")]: true,
  });

  const toggleMenuCollapse = (index) => {
    setMenuCollapseState((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const groupSubmenuByTag = (submenu) => {
    return submenu.reduce((acc, item) => {
      acc[item.tag] = acc[item.tag] || [];
      acc[item.tag].push(item);
      return acc;
    }, {});
  };

  const isParentActive = (submenu, locationPath) => {
    return submenu.some((subItem) => subItem.route === locationPath);
  };

  const handleParentClick = (index, hasSubmenu) => {
    if (hasSubmenu) {
      if (collapsed || modal) {
        setCollapsed(false); // Expand sidebar
        if (modal) onOpenSidebar(); // Open sidebar on mobile
        toggleMenuCollapse(index); // Expand submenu
      } else {
        toggleMenuCollapse(index); // Toggle submenu if already expanded
      }
    } else {
      navigate(visibleNavigations[index].route);
      if (modal) onDismissRequest();
      setScreenStack([...screenStack, visibleNavigations[index].route]);
    }
  };

  return (
    <>
      <Drawer
        variant={modal ? "temporary" : "permanent"}
        anchor="left"
        open={modal ? true : !modal}
        onClose={onDismissRequest}
        sx={{
          zIndex: modal ? 1200 : 100,
          width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
            backgroundColor: "#f3f4f6",
            borderRight: "1px solid #e8e9ec",
            transition: "width 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: modal ? "2px 0 8px rgba(0,0,0,0.2)" : "none",
          },
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        }}
        PaperProps={{ "aria-label": "Sidebar navigation" }}
      >
        {/* Fixed Logo Box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            padding: collapsed ? "7.5px" : "10px 16px",
            backgroundColor: "#f3f4f6",
            minHeight: "56px",
            transition: "all 0.3s ease-in-out",
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          {!collapsed && (
            <Box
              sx={{
                opacity: collapsed ? 0 : 1,
                transition: "opacity 0.3s ease-in-out",
              }}
            >
              <img
                src={Logo}
                alt="Logo"
                style={{ width: LOGO_SIZE, height: "auto" }}
              />
            </Box>
          )}
          {collapsible && (
            <Tooltip
              title={collapsed ? "Expand" : "Collapse"}
              placement="right"
            >
              <IconButton
                onClick={() => {
                  setCollapsed(!collapsed);
                  if (!collapsed && modal) onDismissRequest();
                  if (collapsed && modal) onOpenSidebar();
                }}
                sx={{
                  color: (theme) => theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Scrollable Navigation List */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            paddingBottom: "16px",
          }}
        >
          <List sx={{ padding: "8px 10px" }} aria-label="Main navigation">
            {visibleNavigations.map((navigation, index) => (
              <div key={index} style={{ marginBottom: "4px" }}>
                <Tooltip
                  title={collapsed && !modal ? navigation.text : ""}
                  placement="right"
                  disableInteractive
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleParentClick(index, !!navigation.submenu)
                      }
                      selected={
                        location.pathname === navigation.route ||
                        (navigation.submenu &&
                          isParentActive(navigation.submenu, location.pathname))
                      }
                      sx={{
                        borderRadius: "12px",
                        padding: collapsed ? "12px" : "9px 12px",
                        justifyContent: collapsed ? "center" : "flex-start",
                        "&.Mui-selected": {
                          backgroundColor: "#e5e7eb",
                          color: "#14151a",
                        },
                        "&:hover": {
                          backgroundColor: "#e9eaed",
                          transition: "background-color 0.2s ease-in-out",
                        },
                        transition: "all 0.2s ease-in-out",
                      }}
                      aria-label={navigation.text}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 0 : 40,
                          justifyContent: "center",
                          "& img": {
                            transform: collapsed ? "scale(1.2)" : "scale(1)",
                            transition: "transform 0.2s ease-in-out",
                            filter: (theme) =>
                              location.pathname === navigation.route ||
                              (navigation.submenu &&
                                isParentActive(
                                  navigation.submenu,
                                  location.pathname,
                                ))
                                ? "brightness(1.2)"
                                : "none",
                          },
                        }}
                      >
                        <img
                          src={navigation.icon}
                          alt={`${navigation.text} icon`}
                          style={{ width: 24 }}
                        />
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={navigation.text}
                          primaryTypographyProps={{
                            fontSize: 13.5,
                            color:
                              location.pathname === navigation.route ||
                              (navigation.submenu &&
                                isParentActive(
                                  navigation.submenu,
                                  location.pathname,
                                ))
                                ? "#14151a"
                                : "#565a63",
                            fontWeight:
                              location.pathname === navigation.route ||
                              (navigation.submenu &&
                                isParentActive(
                                  navigation.submenu,
                                  location.pathname,
                                ))
                                ? 700
                                : 500,
                            fontFamily: (theme) => theme.typography.fontFamily,
                          }}
                        />
                      )}
                      {!collapsed &&
                        navigation.submenu &&
                        (menuCollapseState[index] ? (
                          <ExpandLessIcon
                            sx={{
                              color: isParentActive(
                                navigation.submenu,
                                location.pathname,
                              )
                                ? "#14151a"
                                : "#565a63",
                            }}
                          />
                        ) : (
                          <ExpandMoreIcon
                            sx={{
                              color: isParentActive(
                                navigation.submenu,
                                location.pathname,
                              )
                                ? "#14151a"
                                : "#565a63",
                            }}
                          />
                        ))}
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
                {/* Submenu for expanded state */}
                {navigation.submenu && (
                  <Collapse
                    in={menuCollapseState[index] && !collapsed}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        position: "relative",
                        marginTop: "6px",
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: "30px",
                          width: "2px",
                          backgroundColor: "#e8e9ec",
                        },
                      }}
                      aria-label={`${navigation.text} submenu`}
                    >
                      {Object.entries(
                        groupSubmenuByTag(navigation.submenu),
                      ).map(([tag, items], tagIndex) => (
                        <React.Fragment key={tagIndex}>
                          {items.map((subItem, subIndex) => (
                            <ListItem
                              disablePadding
                              key={subIndex}
                              sx={{ pl: collapsed ? 0 : 2 }}
                            >
                              <ListItemButton
                                selected={location.pathname === subItem.route}
                                onClick={() => {
                                  navigate(subItem.route);
                                  if (modal) onDismissRequest();
                                  setScreenStack([
                                    ...screenStack,
                                    subItem.route,
                                  ]);
                                }}
                                sx={{
                                  padding: collapsed ? "8px" : "7px 12px",
                                  borderRadius: "12px",
                                  "&:before": {
                                    content: '""',
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "6px",
                                    height: "6px",
                                    backgroundColor:
                                      location.pathname === subItem.route
                                        ? "#007abe"
                                        : "transparent",
                                    borderRadius: "50%",
                                  },
                                  "&:hover": {
                                    backgroundColor: "#e9eaed",
                                  },
                                  transition: "background-color 0.2s ease-in-out",
                                }}
                                aria-label={subItem.text}
                              >
                                {!collapsed && (
                                  <ListItemText
                                    primary={subItem.text}
                                    primaryTypographyProps={{
                                      fontSize: 13,
                                      pl: 2,
                                      color:
                                        location.pathname === subItem.route
                                          ? "#007abe"
                                          : "#565a63",
                                      fontWeight:
                                        location.pathname === subItem.route
                                          ? 700
                                          : 400,
                                      fontFamily: (theme) =>
                                        theme.typography.fontFamily,
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </List>
                  </Collapse>
                )}
              </div>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

Sidebar.propTypes = {
  collapsible: PropTypes.bool,
  modal: PropTypes.bool,
  onDismissRequest: PropTypes.func,
  screenStack: PropTypes.array,
  setScreenStack: PropTypes.func,
  onOpenSidebar: PropTypes.func,
};

export default Sidebar;
